/**
 * High-level vault CDP operations used by the mobile app.
 * Mirrors frontend useQueries mutation semantics without React.
 */
import type { HttpAgent } from "@icp-sdk/core/agent";
import type { Principal } from "@icp-sdk/core/principal";
import type { ProtocolActor } from "./agent";
import {
	amountToNat,
	type CollateralKey,
	mapCollateralParams,
	mapPortfolioSummary,
	mapPriceFeed,
	mapProtocolParams,
	mapVault,
	natToAmount,
	type PortfolioSummaryUi,
	type PriceFeed,
	type ProtocolParamsUi,
	toCandidCollateral,
	type Vault,
	type CollateralParams,
} from "./candid-helpers";
import type { IcClientConfig } from "./config";
import {
	approveIcrcSpender,
	approveUsdmForProtocol,
	fetchIcrcBalance,
	resolveCollateralLedgerId,
	resolveTransferLedgerId,
	transferIcrc,
	fetchUsdmTransferFee,
	transferUsdmToLiquidatorEscrow,
	type TransferTokenId,
} from "./icrc";
import { unwrapResult } from "./result";

export async function getVaults(actor: ProtocolActor): Promise<Vault[]> {
	return (await actor.getVaults()).map(mapVault);
}

export async function getPriceFeeds(actor: ProtocolActor): Promise<PriceFeed[]> {
	return (await actor.getPriceFeeds()).map(mapPriceFeed);
}

export async function getProtocolParams(
	actor: ProtocolActor,
): Promise<ProtocolParamsUi> {
	return mapProtocolParams(await actor.getProtocolParams());
}

export async function getCollateralParams(
	actor: ProtocolActor,
): Promise<CollateralParams[]> {
	const list = await actor.getCollateralParamsList();
	return list.map(mapCollateralParams);
}

export async function getPortfolioSummary(
	actor: ProtocolActor,
): Promise<PortfolioSummaryUi> {
	return mapPortfolioSummary(await actor.calculatePortfolioSummary());
}

export async function createVault(actor: ProtocolActor): Promise<bigint> {
	return unwrapResult<bigint>(await actor.createVault(), "createVault");
}

/** Owner removes vault when debt and collateral are both zero. */
export async function closeEmptyVault(
	actor: ProtocolActor,
	vaultId: bigint,
): Promise<void> {
	unwrapResult(await actor.closeEmptyVault(vaultId), "closeEmptyVault");
}

export async function depositCollateral(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	params: {
		vaultId: bigint;
		collateralType: CollateralKey;
		amount: number;
	},
): Promise<void> {
	const ledgers = await actor.getLedgerCanisters();
	const ledgerId =
		resolveCollateralLedgerId(config, params.collateralType) ??
		// fallback: still try env-only; protocol ledgers don't include ICP/ck
		null;
	if (ledgerId) {
		await approveIcrcSpender(
			agent,
			ledgerId,
			config.protocolCanisterId,
			params.amount,
		);
	}
	void ledgers;
	unwrapResult(
		await actor.depositCollateral(
			params.vaultId,
			toCandidCollateral(params.collateralType),
			amountToNat(params.amount),
		),
		"depositCollateral",
	);
}

export async function mintUsdm(
	actor: ProtocolActor,
	vaultId: bigint,
	amount: number,
): Promise<void> {
	unwrapResult(
		await actor.mintUSDM(vaultId, amountToNat(amount)),
		"mintUSDM",
	);
}

export async function repayUsdm(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	vaultId: bigint,
	amount: number,
): Promise<void> {
	const ledgers = await actor.getLedgerCanisters();
	await approveUsdmForProtocol(agent, config, ledgers, amount);
	unwrapResult(
		await actor.repayUSDM(vaultId, amountToNat(amount)),
		"repayUSDM",
	);
}

export async function withdrawCollateral(
	actor: ProtocolActor,
	vaultId: bigint,
	collateralType: CollateralKey,
	amount: number,
): Promise<void> {
	unwrapResult(
		await actor.withdrawCollateral(
			vaultId,
			toCandidCollateral(collateralType),
			amountToNat(amount),
		),
		"withdrawCollateral",
	);
}

export type LiquidateVaultOpts = {
	/**
	 * Human USDM to repay. Omit or 0 for protocol full/max (repayE8=0).
	 * Prefer maxRepay from preview for partial liquidations (close factor).
	 */
	repayUsdm?: number;
	/**
	 * Liquidator principal — must match Internet Identity on the protocol actor.
	 * Required for escrow prepay so funds land in the same subaccount burn reads.
	 */
	liquidator: Principal;
	/** If true, repay full debt (repayE8=0). Default false when repayUsdm set. */
	fullClear?: boolean;
};

function normalizeSubaccount(
	raw: Uint8Array | number[] | ArrayBuffer | { [n: number]: number },
): Uint8Array {
	if (raw instanceof Uint8Array) {
		return raw.length === 32 ? raw : Uint8Array.from(raw);
	}
	if (raw instanceof ArrayBuffer) {
		return new Uint8Array(raw);
	}
	return Uint8Array.from(raw as ArrayLike<number>);
}

/**
 * Fixed-spread liquidation: pre-pay USDM to liquidator escrow, then liquidate.
 * When protocol is USDM minter, icrc2_transfer_from is forbidden — escrow is required.
 */
export async function liquidateVault(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	vaultId: bigint,
	opts: LiquidateVaultOpts,
): Promise<{ debtE8: bigint; bonusBps: bigint; collateralValueE8: bigint }> {
	const previewOpt = await actor.previewLiquidation(vaultId);
	const preview = previewOpt[0];
	if (!preview) {
		throw new Error("Vault is not liquidatable");
	}

	// Partial requires explicit repayUsdm. fullClear or missing amount → full debt clear.
	// Protocol maxRepayE8 is close-factor (50%) except dust vaults; residual stays open for others.
	const fullClear = opts.fullClear === true || opts.repayUsdm == null;
	const maxPartialE8 =
		preview.maxRepayE8 > 0n && preview.maxRepayE8 < preview.debtE8
			? preview.maxRepayE8
			: preview.debtE8 > 2n
				? preview.debtE8 / 2n
				: preview.debtE8;
	const maxPartial = natToAmount(maxPartialE8);
	const fullDebt = natToAmount(preview.debtE8);
	let repayHuman: number;
	let repayE8: bigint;
	if (fullClear) {
		// Pass exact full debt so commit clears vault intentionally.
		repayHuman = fullDebt;
		repayE8 = preview.debtE8;
	} else {
		const want = opts.repayUsdm!;
		if (!(want > 0) || !Number.isFinite(want)) {
			throw new Error("Enter a positive USDM repay amount");
		}
		// Cap to close-factor max; never allow partial path to clear full debt
		const cap = maxPartial > 0 ? maxPartial : fullDebt * 0.5;
		repayHuman = Math.min(want, cap);
		repayE8 = amountToNat(repayHuman);
		if (repayE8 <= 0n) throw new Error("Repay amount too small");
		if (repayE8 >= preview.debtE8) {
			// Force residual debt for other keepers (unless vault is 1 e8 dust)
			repayE8 = preview.debtE8 > 1n ? preview.debtE8 - 1n : 1n;
			repayHuman = natToAmount(repayE8);
		} else if (repayE8 > maxPartialE8) {
			repayE8 = maxPartialE8;
			repayHuman = natToAmount(repayE8);
		}
	}

	const ledgers = await actor.getLedgerCanisters();
	const getEscrow = actor.getLiquidatorUsdmEscrowAccount?.bind(actor);

	if (typeof getEscrow !== "function") {
		throw new Error(
			"Protocol missing getLiquidatorUsdmEscrowAccount — upgrade protocol canister, then rebuild SPA.",
		);
	}

	const liquidator = opts.liquidator;
	if (liquidator.isAnonymous()) {
		throw new Error("Connect Internet Identity before liquidating");
	}

	const esc = await getEscrow(liquidator);
	const sub = normalizeSubaccount(esc.subaccount);
	if (sub.length !== 32) {
		throw new Error(
			`Invalid escrow subaccount length ${sub.length} (expected 32)`,
		);
	}

	const usdmLedger = resolveTransferLedgerId(config, "USDM", ledgers);
	if (!usdmLedger) throw new Error("USDM ledger not configured");

	// Escrow must cover hold amount + ledger fee for escrow→treasury Transfer.
	const holdFeeE8 = await fetchUsdmTransferFee(agent, config, ledgers);
	const holdFeeHuman = natToAmount(holdFeeE8);
	const prepayHuman = repayHuman + holdFeeHuman;

	await transferUsdmToLiquidatorEscrow(agent, config, ledgers, prepayHuman, {
		owner: esc.owner,
		subaccount: sub,
	});

	const { fetchIcrcBalanceE8 } = await import("./icrc");
	const balE8 = await fetchIcrcBalanceE8(
		agent,
		usdmLedger,
		esc.owner,
		sub,
	);
	const needE8 = repayE8 + holdFeeE8;
	if (balE8 < needE8) {
		throw new Error(
			`USDM escrow prepay failed: balance ${balE8.toString()} e8 < need ~${needE8.toString()} e8 ` +
				`(repay+holdFee). Principal ${liquidator.toText()}. Re-check wallet USDM and retry.`,
		);
	}

	const result = unwrapResult<{
		debtE8: bigint;
		bonusBps: bigint;
		collateralValueE8: bigint;
	}>(
		await actor.liquidateVaultAmount(vaultId, repayE8),
		"liquidateVault",
	);
	return result;
}

/**
 * Dutch auction fill: approve USDM for the current (or max) auction price, then fill.
 * `currentPriceUsdm` should be the live Dutch price in human USDM units.
 * A 2% buffer covers price drift between approve and the update call.
 */
export async function fillLiquidationAuction(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	auctionId: bigint,
	currentPriceUsdm: number,
	liquidator: Principal,
): Promise<{ debtE8: bigint; bonusBps: bigint; collateralValueE8: bigint }> {
	if (!Number.isFinite(currentPriceUsdm) || currentPriceUsdm <= 0) {
		throw new Error("Invalid auction price for fill");
	}
	if (liquidator.isAnonymous()) {
		throw new Error("Connect Internet Identity before filling auction");
	}
	const ledgers = await actor.getLedgerCanisters();
	const holdFeeE8 = await fetchUsdmTransferFee(agent, config, ledgers);
	const payHuman =
		currentPriceUsdm * 1.002 + natToAmount(holdFeeE8);
	const getEscrow = actor.getLiquidatorUsdmEscrowAccount?.bind(actor);
	if (typeof getEscrow !== "function") {
		throw new Error(
			"Protocol missing getLiquidatorUsdmEscrowAccount — upgrade protocol.",
		);
	}
	const esc = await getEscrow(liquidator);
	const sub = normalizeSubaccount(esc.subaccount);
	await transferUsdmToLiquidatorEscrow(agent, config, ledgers, payHuman, {
		owner: esc.owner,
		subaccount: sub,
	});
	return unwrapResult<{
		debtE8: bigint;
		bonusBps: bigint;
		collateralValueE8: bigint;
	}>(await actor.fillLiquidationAuction(auctionId), "fillLiquidationAuction");
}

export type WalletBalanceToken =
	| "USDM"
	| "ICP"
	| "ckBTC"
	| "ckETH"
	| "MMK"
	| "ckUSDC";

export type WalletBalancesResult = {
	USDM: number;
	ICP: number;
	ckBTC: number;
	ckETH: number;
	MMK: number;
	ckUSDC: number;
	/** Per-token errors (missing ledger, query fail). Empty if all ok. */
	errors: Partial<Record<WalletBalanceToken, string>>;
	/** Resolved ledger canister ids used for each token (if any). */
	ledgerIds: Partial<Record<WalletBalanceToken, string>>;
};

/** Fetch wallet balances for common protocol tokens. */
export async function fetchWalletBalances(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	owner: Principal,
): Promise<WalletBalancesResult> {
	const tokens: WalletBalanceToken[] = [
		"USDM",
		"ICP",
		"ckBTC",
		"ckETH",
		"MMK",
		"ckUSDC",
	];
	let chainLedgers:
		| {
				usdm: [] | [Principal];
				mmk: [] | [Principal];
		  }
		| undefined;
	try {
		chainLedgers = await actor.getLedgerCanisters();
	} catch (e) {
		const msg = e instanceof Error ? e.message : "getLedgerCanisters failed";
		const errors: WalletBalancesResult["errors"] = {};
		for (const t of tokens) errors[t] = msg;
		return {
			USDM: 0,
			ICP: 0,
			ckBTC: 0,
			ckETH: 0,
			MMK: 0,
			ckUSDC: 0,
			errors,
			ledgerIds: {},
		};
	}

	const errors: WalletBalancesResult["errors"] = {};
	const ledgerIds: WalletBalancesResult["ledgerIds"] = {};
	const out: WalletBalancesResult = {
		USDM: 0,
		ICP: 0,
		ckBTC: 0,
		ckETH: 0,
		MMK: 0,
		ckUSDC: 0,
		errors,
		ledgerIds,
	};

	await Promise.all(
		tokens.map(async (token) => {
			const ledgerId = resolveTransferLedgerId(config, token, chainLedgers);
			if (ledgerId) ledgerIds[token] = ledgerId;
			if (!ledgerId) {
				errors[token] = "ledger not configured";
				out[token] = 0;
				return;
			}
			try {
				out[token] = await fetchIcrcBalance(agent, ledgerId, owner);
			} catch (e) {
				errors[token] =
					e instanceof Error ? e.message : "icrc1_balance_of failed";
				out[token] = 0;
			}
		}),
	);
	return out;
}

export async function getTokenBalance(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	owner: Principal,
	token: TransferTokenId,
): Promise<number> {
	const ledgers = await actor.getLedgerCanisters();
	const ledgerId = resolveTransferLedgerId(config, token, ledgers);
	if (!ledgerId) return 0;
	return fetchIcrcBalance(agent, ledgerId, owner);
}

export async function getCollateralWalletBalance(
	agent: HttpAgent,
	config: IcClientConfig,
	owner: Principal,
	collateral: CollateralKey,
): Promise<number> {
	const ledgerId = resolveCollateralLedgerId(config, collateral);
	if (!ledgerId) return 0;
	return fetchIcrcBalance(agent, ledgerId, owner);
}

export async function sendToken(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	token: TransferTokenId,
	to: Principal,
	amount: number,
): Promise<bigint> {
	const ledgers = await actor.getLedgerCanisters();
	const ledgerId = resolveTransferLedgerId(config, token, ledgers);
	if (!ledgerId) {
		throw new Error(`${token} ledger not configured`);
	}
	return transferIcrc(agent, ledgerId, to, amount);
}
